import type { AppConfig } from '@beautyburst/config';

export type MediaResolverKind = 'FALLBACK' | 'R2';

export interface MediaResolver {
  readonly kind: MediaResolverKind;
  resolve(mediaReference: string | null | undefined): string | null;
}

export type MediaResolverConfig = {
  readonly providers: Pick<AppConfig['providers'], 'media'>;
  readonly media: Pick<
    AppConfig['media'],
    'r2AccountId' | 'r2Bucket' | 'r2AccessKeyId' | 'r2SecretAccessKey' | 'publicBaseUrl'
  >;
};

function configured(setting: string | undefined): setting is string {
  return typeof setting === 'string' && setting.trim().length > 0;
}

function normalizePublicBaseUrl(value: string): string | null {
  const baseUrl = value.trim();
  if (baseUrl === '') return null;

  try {
    const url = new URL(baseUrl);
    if (
      (url.protocol !== 'https:' && url.protocol !== 'http:') ||
      url.username !== '' ||
      url.password !== '' ||
      url.search !== '' ||
      url.hash !== ''
    ) {
      return null;
    }
    return url.toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
}

function decodedSegmentIsUnsafe(segment: string): boolean {
  try {
    const decoded = decodeURIComponent(segment);
    return decoded === '.' || decoded === '..' || decoded.includes('/') || decoded.includes('\\');
  } catch {
    return false;
  }
}

export function normalizeMediaObjectKey(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;

  const key = value.trim();
  if (
    key === '' ||
    key.startsWith('/') ||
    key.startsWith('\\') ||
    /^[a-z][a-z\d+.-]*:/i.test(key) ||
    key.includes('\\') ||
    /[\u0000\r\n]/.test(key)
  ) {
    return null;
  }

  const segments = key.split('/');
  if (
    segments.some(
      (segment) => segment === '.' || segment === '..' || decodedSegmentIsUnsafe(segment),
    )
  )
    return null;

  return key;
}

function encodedObjectKey(key: string): string | null {
  try {
    return key.split('/').map(encodeURIComponent).join('/');
  } catch {
    return null;
  }
}

export class FallbackMediaResolver implements MediaResolver {
  readonly kind = 'FALLBACK' as const;

  resolve(_mediaReference: string | null | undefined): null {
    return null;
  }
}

export class R2MediaResolver implements MediaResolver {
  readonly kind = 'R2' as const;
  private readonly publicBaseUrl: string | null;

  constructor(publicBaseUrl: string) {
    this.publicBaseUrl = normalizePublicBaseUrl(publicBaseUrl);
  }

  resolve(mediaReference: string | null | undefined): string | null {
    const key = normalizeMediaObjectKey(mediaReference);
    if (key === null || this.publicBaseUrl === null) return null;

    const encoded = encodedObjectKey(key);
    return encoded === null ? null : `${this.publicBaseUrl}/${encoded}`;
  }
}

function r2SettingsConfigured(media: MediaResolverConfig['media']): boolean {
  return (
    configured(media.r2AccountId) &&
    configured(media.r2Bucket) &&
    configured(media.r2AccessKeyId) &&
    configured(media.r2SecretAccessKey) &&
    configured(media.publicBaseUrl)
  );
}

export function createMediaResolver(config: MediaResolverConfig): MediaResolver {
  if (config.providers.media !== 'r2' || !r2SettingsConfigured(config.media))
    return new FallbackMediaResolver();

  const publicBaseUrl = config.media.publicBaseUrl;
  if (!configured(publicBaseUrl)) return new FallbackMediaResolver();

  const resolver = new R2MediaResolver(publicBaseUrl);
  return resolver.resolve('media-resolver-health-check') === null
    ? new FallbackMediaResolver()
    : resolver;
}
